import { DeleteOutlined } from '@ant-design/icons';
import { Button, Input, Spin } from 'antd';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { getDefaultPaginationSettings, handleResponse, removeCommasAndPercentSign } from '../../../Helper';
import { graphicApi } from '../../../api';
import { SubPermissions, User, getPermissionErrorMessage } from '../../../model/AccountModel';
import { BadgeListInfo, BadgeListInfoPaginated, BadgeStudioFilterBy, BadgeStudioStatus } from '../../../model/BadgeModel';
import { PaginationSetting, ResponseObject, ResponseObjectEntity } from '../../../model/CommonModel';
import { Logger } from '../../../model/LoggingModel';
import { ModalConfirmationList } from '../../common';
import { Modal } from '../../common/Modal/Modal';
import { ListView } from '../ListView/ListView';
import styles from './archiveModal.module.scss';

const user: User = getUser();
const canDeleteBadge: boolean = User.getSubComponentPermission(user, SubPermissions.Badges_Badges_Edit).allowed;
const defaultBadgeStudioFilterBy: BadgeStudioFilterBy = {
	DoubleSided: false,
	Landscape: false,
	OperatorName: [],
	Portrait: false,
	Status: BadgeStudioStatus.Archived,
};

const { Search } = Input;

type Props = {
	onConfirm: () => void;
	onCancel: () => void;
};

const ArchiveModal: React.FC<Props> = ({ onConfirm, onCancel }) => {
	const [loading, setLoading] = useState<boolean>(true);
	const [badgeListInfo, setBadgeListInfo] = useState<BadgeListInfo[]>([]);
	const [pagination, setPagination] = useState<PaginationSetting>({ ...getDefaultPaginationSettings(), PageSize: 10 });
	const [searchText, setSearchText] = useState<string>('');
	const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);

	useEffect(() => {
		getBadgeList(pagination);
	}, []);

	const callSearch = useCallback(
		debounce(async (v: string) => {
			const tempPagination: PaginationSetting = { ...pagination, TotalItems: 0, PageNumber: 1, SearchedValue: v };
			setPagination(tempPagination);
			getBadgeList(tempPagination);
		}, 500),
		[pagination?.SearchedValue]
	);

	const handleOnSetSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget;
		setSearchText(removeCommasAndPercentSign(value));
		callSearch(value);
	};

	const handleOnSearch = value => {
		setSearchText(removeCommasAndPercentSign(value));
		callSearch(value);
	};

	const getBadgeList = (pagination: PaginationSetting) => {
		setLoading(true);
		graphicApi
			.getBadgeList(pagination, defaultBadgeStudioFilterBy)
			.then((response: ResponseObjectEntity<BadgeListInfoPaginated>) => {
				if (!handleResponse(response)) {
					setPagination({ ...pagination, TotalItems: response.Entity.TotalItemsPaginated });
					setBadgeListInfo(response.Entity.BadgesListInfo);
				}
			})
			.catch(e => Logger.writeErrorLog(`${e.name}: ${e.message}`))
			.finally(() => setLoading(false));
	};

	const loadDataFromList = (paginationProp: PaginationSetting = pagination) => {
		getBadgeList(paginationProp);
	};

	const handleRestoreFileButtonClick = () => {
		setLoading(true);
		graphicApi
			.restoreBadges(selectedKeys.map(key => Number(key)))
			.then(response => {
				if (!handleResponse(response)) {
					onConfirm();
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleOnDeleteBadge = () => {
		setLoading(true);
		const badgesToDelete = selectedKeys
			.map(x => {
				const selectedBadge: BadgeListInfo = badgeListInfo.find(y => y.Id === Number(x));
				if (selectedBadge) {
					return { Name: selectedBadge.Name };
				}

				return { Name: '' };
			})
			.filter(x => x.Name !== '');

		const footerContent = (
			<div>
				<br />
				<span>
					<strong>
						{_('ThisActionWillBePermanent')}
						{'.'}
					</strong>
				</span>
				<br />
				<span>{_('YouWontBeAbleToRetrieveFile')}</span>
				<br />
				<span className={styles.redText}>
					<strong>{_('DoYouWishToContinue')}</strong>
				</span>
			</div>
		);
		ModalConfirmationList({
			type: _('Badge'),
			data: badgesToDelete,
			width: '480px',
			footer: footerContent,
			onCancel: () => setLoading(false),
			onConfirm: () => handleDeleteOkConfirmation(selectedKeys),
		});
	};

	const handleDeleteOkConfirmation = async (selectedKeys: React.Key[]) => {
		for (const badgeId of selectedKeys) {
			await onDeleteBadgeRequest(Number(badgeId));
		}

		onConfirm();
	};

	const onDeleteBadgeRequest = async (badgeId: number): Promise<void> => {
		setLoading(true);
		const response: ResponseObject = await graphicApi.deleteBadge(badgeId);
		setLoading(false);
		if (handleResponse(response)) {
			return;
		}
	};

	const title = (
		<div className={styles.modalTitleContainer}>
			<DeleteOutlined />
			<span>{_('Archive')}</span>
		</div>
	);

	const disableButtons = selectedKeys?.length === 0 || !canDeleteBadge;

	return (
		<Modal
			keyboard={true}
			maskClosable={false}
			onCancel={onCancel}
			onClickOk={() => null}
			width={1000}
			title={title}
			visible
			customZoomClass={styles.WithModalZoom}
			hideCloseButton={false}>
			<Spin tip={`${_('Loading')}...`} spinning={loading} size='large'>
				<div className={styles.container}>
					<div className={styles.searchContainer}>
						<Search
							allowClear
							className={styles.searchBar}
							maxLength={50}
							placeholder={_('SearchByName')}
							onSearch={handleOnSearch}
							onChange={handleOnSetSearchText}
							value={searchText}
							enterButton
						/>
						<Button
							id='archiveRestoreBadgeButton'
							title={getPermissionErrorMessage(canDeleteBadge)}
							key='archive-restore-badge-button'
							type='primary'
							disabled={disableButtons}
							onClick={handleRestoreFileButtonClick}>
							{_('RestoreFile')}
						</Button>
						<Button
							id='archiveDeleteBadgeButton'
							title={getPermissionErrorMessage(canDeleteBadge)}
							type='primary'
							disabled={disableButtons}
							onClick={handleOnDeleteBadge}>
							{_('DeleteFile')}
						</Button>
						<span>{_('CurrentFilesWillBeDeleted').replace('%1', '30')}</span>
					</div>
					<div className={styles.listView}>
						<ListView
							badgeListInfo={badgeListInfo}
							pagination={pagination}
							setPagination={setPagination}
							loadData={loadDataFromList}
							selectedKeys={selectedKeys}
							setSelectedKeys={setSelectedKeys}
							showActions={false}
						/>
					</div>
				</div>
			</Spin>
		</Modal>
	);
};

export { ArchiveModal };
